
import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  ShoppingBag, 
  Package, 
  CheckCircle, 
  AlertTriangle,
  ArrowUpRight,
  MousePointer2,
  Activity,
  Zap,
  Clock,
  Plus,
  Star,
  Sparkles,
  Loader2,
  BrainCircuit,
  Lightbulb,
  ArrowDownRight,
  ShieldCheck,
  Timer
} from 'lucide-react';
import { storage } from '../lib/storage';
import { Order, Product, Store } from '../types';
import { Language, translations } from '../lib/i18n';
import { getInventoryInsights } from '../services/geminiService';
import MiniChart from '../components/MiniChart';
import MerchantOnboarding from './MerchantOnboarding';

interface AdminDashboardProps {
  storeId: string;
  lang: Language;
}

interface Insight {
  type: 'warning' | 'success' | 'info';
  title: string;
  advice: string;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ storeId, lang }) => {
  const [store, setStore] = useState<Store | null>(storage.getStoreConfig(storeId));
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const t = translations[lang];

  useEffect(() => {
    const allOrders = storage.getOrders().filter(o => o.storeId === storeId);
    const allProducts = storage.getProducts().filter(p => p.storeId === storeId);
    setOrders(allOrders);
    setProducts(allProducts);
    
    fetchAiInsights(allProducts, allOrders);
  }, [storeId]);

  const fetchAiInsights = async (currentProducts: Product[], currentOrders: Order[]) => {
    setIsLoadingInsights(true);
    const res = await getInventoryInsights(currentProducts, currentOrders);
    setInsights(res);
    setIsLoadingInsights(false);
  };

  const handleOnboardingComplete = () => {
    setStore(storage.getStoreConfig(storeId));
    window.location.reload(); // Quick refresh to update state
  };

  if (store?.status === 'PENDING') {
    if (!store.kyc?.submittedAt) {
      return <MerchantOnboarding store={store} onComplete={handleOnboardingComplete} />;
    }
    
    return (
      <div className="max-w-4xl mx-auto py-20 px-4 space-y-12 animate-in fade-in duration-700">
         <div className="bg-white p-16 rounded-[40px] shadow-2xl border border-gray-100 text-center space-y-10">
            <div className="flex justify-center">
               <div className="w-24 h-24 bg-blue-50 text-blue-600 rounded-[2.5rem] flex items-center justify-center relative">
                  <Timer className="h-12 w-12 animate-pulse" />
                  <div className="absolute -top-2 -right-2 bg-blue-600 text-white p-2 rounded-full shadow-lg">
                     <ShieldCheck className="h-5 w-5" />
                  </div>
               </div>
            </div>
            <div className="space-y-4">
               <h1 className="text-4xl font-black text-gray-900 tracking-tight leading-tight">Your KYC is being Reviewed</h1>
               <p className="text-gray-500 text-lg font-medium max-w-xl mx-auto leading-relaxed">
                  Bhai, Lynqar team aapki business details review kar rahi hai. Account activate hone ke baad hi aap live sales kar payenge. Humen aam taur par <span className="text-blue-600 font-bold">24-48 ghante</span> lagte hain.
               </p>
            </div>
            <div className="p-8 bg-gray-50 rounded-[2rem] border border-gray-100 grid grid-cols-2 gap-8 text-left">
               <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Status</p>
                  <p className="font-bold text-gray-900 flex items-center gap-2">
                     <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div> Awaiting Admin Approval
                  </p>
               </div>
               <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Submitted On</p>
                  <p className="font-bold text-gray-900">{new Date(store.kyc.submittedAt).toLocaleDateString()}</p>
               </div>
            </div>
            <p className="text-xs text-gray-400 font-medium">Tab tak aap dashboard ka look aur feel manage kar sakte hain.</p>
         </div>
      </div>
    );
  }

  const totalSales = orders
    .filter(o => o.status !== 'CANCELLED')
    .reduce((sum, o) => sum + o.total, 0);

  // Mock historical data for charts
  const salesTrend = [400, 800, 600, 1200, 900, 1500, 2100];
  const orderTrend = [5, 8, 4, 12, 7, 15, 18];

  const stats = [
    { label: t.revenue, value: `$${totalSales.toLocaleString()}`, icon: DollarSign, trend: '+12.5%', trendType: 'up', color: 'text-emerald-600', bg: 'bg-emerald-50', chartData: salesTrend },
    { label: t.orders, value: orders.length.toString(), icon: ShoppingBag, trend: '+8.1%', trendType: 'up', color: 'text-blue-600', bg: 'bg-blue-50', chartData: orderTrend },
    { label: 'Conversion', value: '3.2%', icon: MousePointer2, trend: '-0.4%', trendType: 'down', color: 'text-orange-600', bg: 'bg-orange-50', chartData: [3, 4, 2, 5, 3, 3, 4] },
    { label: 'Traffic', value: '8.4k', icon: Users, trend: '+14%', trendType: 'up', color: 'text-indigo-600', bg: 'bg-indigo-50', chartData: [1000, 2000, 1500, 3000, 2500, 4000, 5000] },
  ];

  const liveActivity = [
    { id: 1, text: "Customer from Mumbai just viewed 'Lynq Book Pro'", time: "Just now", type: "view" },
    { id: 2, text: "New Order #ORD-4412 from Delhi", time: "2 mins ago", type: "sale" },
    { id: 3, text: "AI Assistant resolved a query for 'Lynq Pods'", time: "5 mins ago", type: "ai" }
  ];

  return (
    <div className="space-y-8 animate-in fade-in duration-700 pb-10">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-8 rounded-[40px] text-white flex flex-col md:flex-row justify-between items-center gap-6 shadow-2xl relative overflow-hidden">
         <div className="absolute top-0 right-0 p-10 opacity-10"><Zap className="h-32 w-32" /></div>
         <div className="space-y-2 text-center md:text-left relative">
            <h2 className="text-3xl font-black tracking-tight">{t.welcomeBack}</h2>
            <p className="text-blue-100 font-medium">Your store is <span className="text-white font-black">Active</span> and sync'd with Lynqar Cloud.</p>
         </div>
         <div className="flex gap-4 relative">
            <button onClick={() => fetchAiInsights(products, orders)} className="bg-white text-blue-600 px-8 py-4 rounded-2xl font-black transition-all hover:scale-105 flex items-center gap-2 shadow-lg">
               {isLoadingInsights ? <Loader2 className="h-4 w-4 animate-spin" /> : <BrainCircuit className="h-4 w-4" />}
               AI Intelligence
            </button>
         </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-xl hover:shadow-2xl transition-all group overflow-hidden">
            <div className="flex justify-between items-start mb-4">
              <div className={`${stat.bg} ${stat.color} p-4 rounded-2xl group-hover:rotate-12 transition-transform`}><stat.icon className="h-6 w-6" /></div>
              <div className="text-right">
                <span className={`text-[10px] font-black px-2 py-1 rounded-full flex items-center gap-1 ${stat.trendType === 'up' ? 'bg-emerald-50 text-emerald-600' : 'bg-orange-50 text-orange-600'}`}>
                  {stat.trendType === 'up' ? <TrendingUp className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                  {stat.trend}
                </span>
              </div>
            </div>
            <p className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em]">{stat.label}</p>
            <h3 className="text-3xl font-black text-gray-900 mt-2 mb-6">{stat.value}</h3>
            
            <div className="h-10 -mx-8 -mb-4 px-8 opacity-60 group-hover:opacity-100 transition-opacity">
               <MiniChart data={stat.chartData} color={stat.trendType === 'up' ? '#10b981' : '#f97316'} height={40} />
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
         <div className="lg:col-span-2 space-y-8">
            <section className="bg-white p-10 rounded-[40px] border border-blue-100 shadow-xl shadow-blue-50/50 space-y-8">
               <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <div className="p-3 bg-blue-600 text-white rounded-2xl shadow-lg shadow-blue-200">
                        <Sparkles className="h-6 w-6" />
                     </div>
                     <div>
                        <h3 className="text-xl font-black text-gray-900 tracking-tight">AI Business Advisor</h3>
                        <p className="text-[10px] text-blue-600 font-black uppercase tracking-widest">Powered by Gemini Intelligent Engine</p>
                     </div>
                  </div>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {isLoadingInsights ? (
                    Array(3).fill(0).map((_, i) => (
                      <div key={i} className="h-40 bg-gray-50 rounded-3xl animate-pulse"></div>
                    ))
                  ) : (
                    insights.map((insight, idx) => (
                      <div key={idx} className={`p-6 rounded-[2.5rem] border-2 transition-all hover:scale-[1.02] ${
                        insight.type === 'warning' ? 'bg-orange-50 border-orange-100' : 
                        insight.type === 'success' ? 'bg-emerald-50 border-emerald-100' : 
                        'bg-blue-50 border-blue-100'
                      }`}>
                         <div className="flex justify-between items-start mb-4">
                            <div className={`p-2 rounded-xl ${
                              insight.type === 'warning' ? 'bg-orange-200 text-orange-700' : 
                              insight.type === 'success' ? 'bg-emerald-200 text-emerald-700' : 
                              'bg-blue-200 text-blue-700'
                            }`}>
                               {insight.type === 'warning' ? <AlertTriangle className="h-4 w-4" /> : 
                                insight.type === 'success' ? <TrendingUp className="h-4 w-4" /> : 
                                <Lightbulb className="h-4 w-4" />}
                            </div>
                         </div>
                         <h4 className="font-black text-gray-900 mb-2 leading-tight">{insight.title}</h4>
                         <p className="text-xs text-gray-500 font-medium leading-relaxed">{insight.advice}</p>
                      </div>
                    ))
                  )}
               </div>
            </section>

            <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl space-y-8">
               <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                     <Activity className="h-6 w-6 text-blue-600" />
                     <h3 className="text-xl font-black text-gray-900 tracking-tight">Real-time Pulse</h3>
                  </div>
                  <span className="flex items-center gap-2 text-[10px] font-black text-emerald-500 uppercase tracking-widest animate-pulse">
                     <div className="w-2 h-2 bg-emerald-500 rounded-full"></div> Live Stream
                  </span>
               </div>
               <div className="space-y-4">
                  {liveActivity.map((act) => (
                    <div key={act.id} className="p-5 bg-gray-50 rounded-3xl border border-gray-100 flex items-center justify-between group hover:border-blue-200 transition-colors">
                       <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-xl ${act.type === 'sale' ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600'}`}>
                             <Package className="h-4 w-4" />
                          </div>
                          <div>
                             <p className="text-sm font-bold text-gray-900">{act.text}</p>
                             <div className="flex items-center gap-2 text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">
                                <Clock className="h-3 w-3" /> {act.time}
                             </div>
                          </div>
                       </div>
                       <ArrowUpRight className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  ))}
               </div>
            </div>
         </div>

         <div className="space-y-8">
            <div className="bg-gray-900 p-10 rounded-[40px] text-white shadow-2xl space-y-8 relative overflow-hidden group">
               <div className="absolute inset-0 bg-blue-600/5 backdrop-blur-3xl"></div>
               <div className="relative space-y-6">
                  <h3 className="text-xl font-black tracking-tight">Quick Actions</h3>
                  <div className="grid grid-cols-2 gap-4">
                     <button className="p-6 bg-white/5 border border-white/10 rounded-3xl text-center space-y-3 hover:bg-white/10 transition-all">
                        <Plus className="h-6 w-6 mx-auto text-blue-400" />
                        <p className="text-[10px] font-black uppercase tracking-widest">Add Product</p>
                     </button>
                     <button className="p-6 bg-white/5 border border-white/10 rounded-3xl text-center space-y-3 hover:bg-white/10 transition-all">
                        <Users className="h-6 w-6 mx-auto text-indigo-400" />
                        <p className="text-[10px] font-black uppercase tracking-widest">Customers</p>
                     </button>
                     <button className="p-6 bg-white/5 border border-white/10 rounded-3xl text-center space-y-3 hover:bg-white/10 transition-all">
                        <Star className="h-6 w-6 mx-auto text-amber-400" />
                        <p className="text-[10px] font-black uppercase tracking-widest">Reviews</p>
                     </button>
                     <button className="p-6 bg-white/5 border border-white/10 rounded-3xl text-center space-y-3 hover:bg-white/10 transition-all">
                        <AlertTriangle className="h-6 w-6 mx-auto text-red-400" />
                        <p className="text-[10px] font-black uppercase tracking-widest">Support</p>
                     </button>
                  </div>
               </div>
            </div>

            <div className="bg-blue-600 p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden">
               <div className="absolute -right-4 -bottom-4 p-10 opacity-10"><DollarSign className="h-24 w-24" /></div>
               <h3 className="text-xl font-black mb-4">Payout Wallet</h3>
               <p className="text-blue-100 text-sm mb-6">Your next scheduled payout is in 2 days.</p>
               <div className="flex justify-between items-end">
                  <div>
                     <p className="text-[10px] font-black uppercase tracking-widest text-blue-200">Total Net</p>
                     <p className="text-3xl font-black">$4,281.00</p>
                  </div>
                  <button className="bg-white text-blue-600 px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:scale-105 transition-all">Withdraw</button>
               </div>
            </div>
         </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
